import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import {
  fetchParentStudents,
  getActiveStudentId
} from '../../lib/parentStudents'

export default function ParentHome() {
  const [student, setStudent] = useState(null)
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        setLoading(false)
        return
      }

      const { students } = await fetchParentStudents(user.id)

      const activeId = getActiveStudentId(students)

      const s = students.find(item => item.id === activeId)

      if (!s) {
        setLoading(false)
        return
      }

      setStudent(s)

      const [
        { data: grades },
        { data: att },
        { data: hw }
      ] = await Promise.all([
        supabase
          .from('grades')
          .select('*')
          .eq('student_id', s.id),

        supabase
          .from('attendance')
          .select('*')
          .eq('student_id', s.id),

        supabase
          .from('homework')
          .select('*')
          .eq('student_id', s.id),
      ])

      const avgGrade = grades?.length
        ? Math.round(
            grades.reduce(
              (a, g) => a + (g.score / g.total * 100),
              0
            ) / grades.length
          )
        : null

      const attPct = att?.length
        ? Math.round(
            att.filter(a => a.status === 'present').length /
            att.length * 100
          )
        : null

      const hwPct = hw?.length
        ? Math.round(
            hw.filter(h => h.status === 'done').length /
            hw.length * 100
          )
        : null

      const lastGrade = grades?.sort(
        (a, b) =>
          new Date(b.created_at) -
          new Date(a.created_at)
      )[0]

      setStats({
        avgGrade,
        attPct,
        hwPct,
        lastGrade,
        totalGrades: grades?.length
      })

      setLoading(false)
    }

    fetchData()

    window.addEventListener(
      'parent-student-changed',
      fetchData
    )

    return () => {
      window.removeEventListener(
        'parent-student-changed',
        fetchData
      )
    }
  }, [])

  const pctColor = p =>
    p == null
      ? 'var(--text3)'
      : p >= 80
        ? 'var(--success)'
        : p >= 60
          ? 'var(--warning)'
          : 'var(--danger)'

  const pctClass = p =>
    p == null
      ? ''
      : p >= 80
        ? 'badge-success'
        : p >= 60
          ? 'badge-warning'
          : 'badge-danger'

  if (loading) {
    return <div className="empty">Loading...</div>
  }

  if (!student) {
    return (
      <div className="empty">
        <div style={{
          fontSize: 48,
          marginBottom: 12
        }}>
          🔗
        </div>

        <div style={{
          fontWeight: 500,
          marginBottom: 8
        }}>
          No linked students
        </div>

        <div style={{
          color: 'var(--text3)'
        }}>
          Open Settings to check your linked students.
        </div>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 20
    }}>

      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}
      >
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--accent-bg)',
          color: 'var(--accent)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          fontWeight: 600,
          flexShrink: 0
        }}>
          {student.name.charAt(0).toUpperCase()}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 18,
            fontWeight: 600
          }}>
            {student.name}
          </div>

          {student.grade && (
            <div style={{
              color: 'var(--text3)',
              fontSize: 13,
              marginTop: 2
            }}>
              {student.grade}
            </div>
          )}

          <div style={{
            color: 'var(--text3)',
            fontSize: 12,
            marginTop: 2
          }}>
            {student.student_code}
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns:
          'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12
      }}>
        {[
          {
            label: 'Avg. Grade',
            val: stats?.avgGrade != null
              ? stats.avgGrade + '%'
              : '—',
            color: pctColor(stats?.avgGrade)
          },
          {
            label: 'Attendance',
            val: stats?.attPct != null
              ? stats.attPct + '%'
              : '—',
            color: pctColor(stats?.attPct)
          },
          {
            label: 'Homework',
            val: stats?.hwPct != null
              ? stats.hwPct + '%'
              : '—',
            color: pctColor(stats?.hwPct)
          },
          {
            label: 'Total Exams',
            val: stats?.totalGrades ?? 0,
            color: 'var(--text)'
          },
        ].map(stat => (
          <div
            key={stat.label}
            className="card"
            style={{ textAlign: 'center' }}
          >
            <div style={{
              fontSize: 13,
              color: 'var(--text3)',
              marginBottom: 6
            }}>
              {stat.label}
            </div>

            <div style={{
              fontSize: 26,
              fontWeight: 600,
              color: stat.color
            }}>
              {stat.val}
            </div>
          </div>
        ))}
      </div>

      {stats?.lastGrade && (
        <div className="card">
          <div style={{
            fontSize: 13,
            color: 'var(--text3)',
            marginBottom: 10
          }}>
            Latest Exam
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12
          }}>
            <div style={{
              fontWeight: 500,
              minWidth: 0
            }}>
              {stats.lastGrade.exam_name}
            </div>

            <div style={{
              textAlign: 'right',
              flexShrink: 0
            }}>
              <span className={`badge ${
                pctClass(
                  Math.round(
                    stats.lastGrade.score /
                    stats.lastGrade.total *
                    100
                  )
                )
              }`}>
                {stats.lastGrade.score}/
                {stats.lastGrade.total} —
                {Math.round(
                  stats.lastGrade.score /
                  stats.lastGrade.total *
                  100
                )}%
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}